import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import DateRangeFilter from "./shared/DateRangeFilter";
import KpiCards from "./shared/KpiCards";
import AnalyticsChart from "./shared/AnalyticsChart";
import AnalyticsMetricList from "./shared/AnalyticsMetricList";
import AnalyticsWorkspaceHero from "./shared/AnalyticsWorkspaceHero";
import EntityMultiSelect from "./shared/EntityMultiSelect";
import useAnalyticsDashboardData from "./shared/useAnalyticsDashboardData";
import {
  buildDistributionSummary,
  DistributionTooltip,
  formatDateValue,
  formatNumber,
  formatRangeLabel,
  getPeakEntry,
  getRangeDayCount,
  getTopItem,
  safeNumber,
  TimeSeriesTooltip,
} from "./shared/analyticsUtils";

const AREA_STROKE = "#2563EB";
const AREA_FILL = "#60A5FA";
const STATUS_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#0EA5E9"];
const SOURCE_COLORS = ["#2563EB", "#0EA5E9", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444"];
const METHOD_COLORS = ["#10B981", "#2563EB", "#F59E0B", "#8B5CF6", "#EF4444", "#0EA5E9"];

function AppointmentsSection(props) {
  const [selectedBranches, setSelectedBranches] = useState(props.selectedBranches || []);
  const [selectedSpecialities, setSelectedSpecialities] = useState(props.selectedSpecialities || []);
  const extraParams = useMemo(
    () => ({
      hospital_ids: selectedBranches.map((b) => b.id),
      speciality_ids: selectedSpecialities.map((s) => s.id),
    }),
    [selectedBranches, selectedSpecialities]
  );
  const { data, loading, currentStartDate, currentEndDate, handleDateChange } = useAnalyticsDashboardData({
    dataUrl: props.dataUrl,
    startDate: props.startDate,
    endDate: props.endDate,
    extraParams,
  });
  const locale = props.locale || "en";
  const labels = props.labels;
  const selectedRangeLabel = formatRangeLabel(currentStartDate, currentEndDate, locale);
  const rangeDayCount = getRangeDayCount(currentStartDate, currentEndDate);
  const dailyVolume = data?.daily_volume || [];
  const statusFunnel = data?.status_funnel || [];
  const sourceBreakdown = data?.source_breakdown || [];
  const methodBreakdown = data?.method_breakdown || [];
  const totalAppointments = safeNumber(data?.kpis?.[0]?.value);
  const activeDays = dailyVolume.filter((entry) => safeNumber(entry.count) > 0).length;
  const averageDaily = rangeDayCount > 0 ? Math.round(totalAppointments / rangeDayCount) : 0;
  const peakDay = getPeakEntry(dailyVolume);
  const topStatus = getTopItem(statusFunnel);
  const topSource = getTopItem(sourceBreakdown);
  const topMethod = getTopItem(methodBreakdown);
  const loadingLabel = labels.loading || "...";

  const heroItems = [
    {
      label: labels.selectedWindow,
      value: selectedRangeLabel || loadingLabel,
    },
    {
      label: labels.totalInWindow,
      value: loading ? loadingLabel : formatNumber(totalAppointments, locale),
      hint: labels.appointmentsUnit,
    },
    {
      label: labels.topSource,
      value: loading ? loadingLabel : topSource?.label || labels.noData,
      hint: topSource ? `${formatNumber(topSource.value, locale)} ${labels.appointmentsUnit}` : null,
    },
    {
      label: labels.topMethod,
      value: loading ? loadingLabel : topMethod?.label || labels.noData,
      hint: topMethod ? `${formatNumber(topMethod.value, locale)} ${labels.appointmentsUnit}` : null,
    },
  ];

  const filterSummaryItems = [
    {
      label: labels.leadingStatus,
      value: loading ? loadingLabel : topStatus?.label || labels.noData,
      hint: topStatus ? `${formatNumber(topStatus.value, locale)} ${labels.appointmentsUnit}` : null,
    },
    {
      label: labels.averageDaily,
      value: loading ? loadingLabel : formatNumber(averageDaily, locale),
      hint: labels.appointmentsUnit,
    },
    {
      label: labels.peakDay,
      value: loading ? loadingLabel : peakDay ? formatNumber(peakDay.count, locale) : labels.noData,
      hint: peakDay ? formatDateValue(peakDay.date, locale, { day: "2-digit", month: "short", year: "numeric" }) : null,
    },
  ];

  const trendItems = [
    {
      label: labels.peakDay,
      value: peakDay ? formatNumber(peakDay.count, locale) : labels.noData,
      hint: peakDay ? formatDateValue(peakDay.date, locale, { day: "2-digit", month: "short", year: "numeric" }) : null,
    },
    {
      label: labels.averageDaily,
      value: formatNumber(averageDaily, locale),
      hint: selectedRangeLabel,
    },
    {
      label: labels.activeDays,
      value: formatNumber(activeDays, locale),
      hint: labels.activeDaysUnit,
    },
  ];

  const statusSummary = buildDistributionSummary(statusFunnel, locale, labels.appointmentsUnit);
  const sourceSummary = buildDistributionSummary(sourceBreakdown, locale, labels.appointmentsUnit);
  const methodSummary = buildDistributionSummary(methodBreakdown, locale, labels.appointmentsUnit);

  return (
    <div className="analytics-workspace">
      <AnalyticsWorkspaceHero
        eyebrow={labels.heroEyebrow}
        title={labels.heroTitle}
        description={labels.heroDescription}
        items={heroItems}
      />

      <DateRangeFilter
        startDate={currentStartDate}
        endDate={currentEndDate}
        labels={labels}
        onChange={handleDateChange}
        title={labels.filterTitle}
        description={labels.filterDescription}
        rangeLabel={selectedRangeLabel}
        summaryItems={filterSummaryItems}
        entityFilters={
          <div className="row">
            <div className="col-md-6">
              <EntityMultiSelect
                label={labels.branchesLabel}
                placeholder={labels.branchesPlaceholder}
                searchUrl={props.branchSearchUrl}
                selected={selectedBranches}
                onChange={setSelectedBranches}
                noResultsLabel={labels.branchesNoResults}
                minCharsHint={labels.filterMinChars}
                searchingLabel={labels.filterSearching}
                clearAllLabel={labels.filterClearAll}
              />
            </div>
            <div className="col-md-6">
              <EntityMultiSelect
                label={labels.polisLabel}
                placeholder={labels.polisPlaceholder}
                searchUrl={props.specialitySearchUrl}
                selected={selectedSpecialities}
                onChange={setSelectedSpecialities}
                noResultsLabel={labels.polisNoResults}
                minCharsHint={labels.filterMinChars}
                searchingLabel={labels.filterSearching}
                clearAllLabel={labels.filterClearAll}
              />
            </div>
          </div>
        }
      />


      <KpiCards kpis={data?.kpis} loading={loading} periodLabel={labels.vsPreviousPeriod} locale={locale} />

      <div className="row">
        <div className="col-12 mb-4">
          <AnalyticsChart
            title={labels.dailyVolume}
            description={labels.dailyVolumeDescription}
            loading={loading}
            isEmpty={!dailyVolume.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-calendar-note"
            aside={<AnalyticsMetricList items={trendItems} emptyLabel={labels.noData} />}
            loadingHeight={340}
          >
            <ResponsiveContainer width="100%" height={340}>
              <AreaChart data={dailyVolume} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="analytics-appointments-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={AREA_FILL} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={AREA_FILL} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="4 4" stroke="rgba(148, 163, 184, 0.28)" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => formatDateValue(value, locale, { day: "2-digit", month: "short" })}
                  minTickGap={24}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                <Tooltip content={<TimeSeriesTooltip locale={locale} unitLabel={labels.appointmentsUnit} />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke={AREA_STROKE}
                  strokeWidth={3}
                  fill="url(#analytics-appointments-area)"
                  fillOpacity={1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>
      </div>

      <div className="row">
        <div className="col-xl-4 col-md-6 mb-4">
          <AnalyticsChart
            title={labels.statusFunnel}
            description={labels.statusFunnelDescription}
            loading={loading}
            isEmpty={!statusFunnel.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-assignment-check"
            footer={<AnalyticsMetricList items={statusSummary} emptyLabel={labels.noData} compact={true} />}
            loadingHeight={280}
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={statusFunnel} layout="vertical" margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid horizontal strokeDasharray="4 4" stroke="rgba(148, 163, 184, 0.24)" />
                <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
                <YAxis dataKey="label" type="category" width={90} tickLine={false} axisLine={false} />
                <Tooltip content={<DistributionTooltip locale={locale} unitLabel={labels.appointmentsUnit} />} />
                <Bar dataKey="value" radius={[0, 10, 10, 0]}>
                  {statusFunnel.map((entry, index) => (
                    <Cell key={`${entry.label}-${index}`} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>

        <div className="col-xl-4 col-md-6 mb-4">
          <AnalyticsChart
            title={labels.sourceBreakdown}
            description={labels.sourceBreakdownDescription}
            loading={loading}
            isEmpty={!sourceBreakdown.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-globe"
            footer={<AnalyticsMetricList items={sourceSummary} emptyLabel={labels.noData} compact={true} />}
            loadingHeight={280}
          >
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={sourceBreakdown}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={72}
                  outerRadius={102}
                  paddingAngle={3}
                  stroke="none"
                >
                  {sourceBreakdown.map((entry, index) => (
                    <Cell key={`${entry.label}-${index}`} fill={SOURCE_COLORS[index % SOURCE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<DistributionTooltip locale={locale} unitLabel={labels.appointmentsUnit} />} />
              </PieChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>

        <div className="col-xl-4 col-md-6 mb-4">
          <AnalyticsChart
            title={labels.methodBreakdown}
            description={labels.methodBreakdownDescription}
            loading={loading}
            isEmpty={!methodBreakdown.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-device-hub"
            footer={<AnalyticsMetricList items={methodSummary} emptyLabel={labels.noData} compact={true} />}
            loadingHeight={280}
          >
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={methodBreakdown}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={72}
                  outerRadius={102}
                  paddingAngle={3}
                  stroke="none"
                >
                  {methodBreakdown.map((entry, index) => (
                    <Cell key={`${entry.label}-${index}`} fill={METHOD_COLORS[index % METHOD_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<DistributionTooltip locale={locale} unitLabel={labels.appointmentsUnit} />} />
              </PieChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>
      </div>
    </div>
  );
}

export default AppointmentsSection;
